// node_nodules
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../setup";

// Define a service using a base URL and expected endpoints
export const foodStationApi = createApi({
  reducerPath: "foodStationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "FoodStations",
    "FoodStation",
    "DeletedFoodStations",
    "StationManagers",
  ],
  endpoints: (builder) => ({
    getStationList: builder.query({
      query: (subDomain) => {
        return {
          url: `/station/list`,
          method: "GET",
          params: { subdomain: subDomain },
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.map(({ id }) => ({ type: "FoodStations", id })),
              { type: "FoodStations", id: "LIST" },
            ]
          : [{ type: "FoodStations", id: "LIST" }];
      },
    }),
    createStation: builder.mutation({
      query: ({ subdomain, data }) => {
        return {
          url: `/station/create`,
          method: "POST",
          body: { ...data, subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    getStationData: builder.query({
      query: ({ id, subdomain }) => {
        return {
          url: `/station/export/${id}`,
          method: "GET",
          params: { subdomain },
        };
      },
      providesTags: (result, error, id) => [{ type: "FoodStation", id }],
    }),
    importStation: builder.mutation({
      query: (payload) => {
        return {
          url: `/station/import`,
          method: "POST",
          body: { ...payload },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    updateStation: builder.mutation({
      query: ({ id, subdomain, data }) => {
        return {
          url: `/station/${id}`,
          method: "PATCH",
          body: data,
          params: { subdomain: subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    updateSoundActive: builder.mutation({
      query: ({ id, subdomain, data }) => {
        return {
          url: `/station/sound/active/${id}`,
          method: "PATCH",
          body: data,
          params: { subdomain: subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    deleteStation: builder.mutation({
      query: ({ id, subdomain }) => {
        return {
          url: `/station/${id}`,
          method: "DELETE",
          params: { subdomain: subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    uploadImage: builder.mutation({
      query: ({ id, subdomain, data, imageType }) => {
        const formData = new FormData();
        formData.append("file", data);
        formData.append("type", imageType);
        return {
          url: `/station/image/upload/${id}`,
          method: "POST",
          body: formData,
          params: { subdomain: subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    uploadSound: builder.mutation({
      query: ({ id, subdomain, data }) => {
        const formData = new FormData();
        formData.append("file", data);
        return {
          url: `/station/sound/upload/${id}`,
          method: "POST",
          body: formData,
          params: { subdomain: subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    getDeletedStationList: builder.query({
      query: (subDomain) => {
        return {
          url: `/station/deleted`,
          method: "GET",
          params: { subdomain: subDomain },
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.map(({ id }) => ({ type: "DeletedFoodStations", id })),
              { type: "DeletedFoodStations", id: "LIST" },
            ]
          : [{ type: "DeletedFoodStations", id: "LIST" }];
      },
    }),
    recoverStation: builder.mutation({
      query: ({ id, subdomain }) => {
        return {
          url: `/station/recover/${id}`,
          method: "PATCH",
          params: { subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStations", id: "LIST" }],
    }),
    permanentlyDeleteStation: builder.mutation({
      query: ({ id, subdomain }) => {
        return {
          url: `/station/permanently/${id}`,
          method: "DELETE",
          params: { subdomain: subdomain },
        };
      },
    }),
    getSubStationList: builder.query({
      query: ({ id, subdomain }) => {
        return {
          url: `/station/sub`,
          method: "GET",
          params: { id, subdomain },
        };
      },
    }),
    getStationManagers: builder.query({
      query: ({ id, subdomain }) => {
        return {
          url: `/station/${id}/managers`,
          method: "GET",
          params: { subdomain },
        };
      },
      providesTags: (result, error, { id }) => [
        { type: "StationManagers", id },
      ],
    }),
    updateStationManagerNotification: builder.mutation({
      query: ({ id, userId, subdomain, sms_enabled }) => {
        return {
          url: `/station/${id}/managers/${userId}`,
          method: "PATCH",
          body: { sms_enabled },
          params: { subdomain },
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "StationManagers", id },
      ],
    }),
  }),
});

export const {
  useGetStationListQuery,
  useGetSubStationListQuery,
  useLazyGetDeletedStationListQuery,
  useLazyGetStationDataQuery,
  useImportStationMutation,
  useCreateStationMutation,
  useUpdateStationMutation,
  useUpdateSoundActiveMutation,
  useDeleteStationMutation,
  useUploadImageMutation,
  useUploadSoundMutation,
  useRecoverStationMutation,
  usePermanentlyDeleteStationMutation,
  useGetStationManagersQuery,
  useUpdateStationManagerNotificationMutation,
} = foodStationApi;
